import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollZoom } from '../hooks/useScrollZoom';

const AboutSection = styled.section`
  padding: 5rem 0;
  background: var(--bg-secondary);

  @media (max-width: 768px) {
    padding: 3rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 600;
  color: var(--brand-primary);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
`;

const AboutContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const AboutTitle = styled.h3`
  font-size: 2rem;
  color: var(--brand-primary);
  margin-bottom: 1rem;
`;

const AboutText = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.8;
`;

const Features = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 1.5rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.8rem;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 1.2rem;
  background: var(--bg-elevated);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow);
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 20px var(--shadow-strong);
    border-color: var(--brand-light);
  }

  i {
    color: var(--brand-primary);
    font-size: 1.3rem;
    min-width: 20px;
    text-align: center;
    transition: transform 0.3s ease;
  }

  &:hover i {
    transform: scale(1.2) rotate(8deg);
  }

  span {
    font-weight: 500;
    color: var(--text-primary);
    font-size: 0.95rem;
    text-align: left;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
    padding: 1rem;
    gap: 8px;
    
    i {
      font-size: 1.5rem;
    }
    
    span {
      font-size: 0.9rem;
    }
  }
`;

const About = () => {
  const { t } = useLanguage();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollZoom = useScrollZoom({ disabled: reducedMotion });

  return (
    <AboutSection id="about">
      <Container>
        <SectionHeader data-aos="fade-up" ref={scrollZoom.ref} style={scrollZoom.style}>
          <SectionTitle>
            {t("About Our Homestay", "Về Homestay của chúng tôi")}
          </SectionTitle>
          <SectionSubtitle>
            {t("Discover the perfect blend of comfort and adventure", "Khám phá sự kết hợp hoàn hảo giữa sự thoải mái và phiêu lưu")}
          </SectionSubtitle>
        </SectionHeader>
        
        <AboutContent data-aos="fade-up" data-aos-delay="100">
          <AboutTitle>
            {t("Your Home Away From Home", "Ngôi nhà thứ hai của bạn")}
          </AboutTitle>
          <AboutText>
            {t(
              "Nestled in the beautiful mountains of Da Lat, Hoang Uyen Homestay offers a unique experience that combines the comfort of home with the adventure of exploring Vietnam's most charming highland city.",
              "Tọa lạc giữa những ngọn núi xinh đẹp của Đà Lạt, Hoàng Uyên Homestay mang đến trải nghiệm độc đáo kết hợp sự thoải mái của ngôi nhà với cuộc phiêu lưu khám phá thành phố cao nguyên quyến rũ nhất Việt Nam."
            )}
          </AboutText>
          
          <Features>
            {[
              { icon: 'fa-wifi', en: 'Free WiFi', vi: 'WiFi miễn phí' },
              { icon: 'fa-car', en: 'Car Parking', vi: 'Bãi đỗ xe' },
              { icon: 'fa-tshirt', en: 'Laundry Service', vi: 'Dịch vụ giặt ủi' },
              { icon: 'fa-utensils', en: 'Kitchen Access', vi: 'Bếp sử dụng' },
              { icon: 'fa-mountain', en: 'Mountain View', vi: 'View núi rừng' },
              { icon: 'fa-coffee', en: 'Coffee & Tea', vi: 'Cà phê & Trà' },
              { icon: 'fa-concierge-bell', en: '24/7 Support', vi: 'Hỗ trợ 24/7' },
              { icon: 'fa-map-marker-alt', en: 'Near Central City', vi: 'Gần trung tâm thành phố' },
            ].map((f, i) => (
              <FeatureItem key={i} data-aos="fade-up" data-aos-delay={i * 80}>
                <i className={`fas ${f.icon}`}></i>
                <span>{t(f.en, f.vi)}</span>
              </FeatureItem>
            ))}
          </Features>
        </AboutContent>
      </Container>
    </AboutSection>
  );
};

export default About;